// ---------------------------------------------------------------------------
// AuthLayout, ported from the design system (R10)
//
// The frame for a page that stands alone outside the console: sign-in, the
// admin sign-in in German (lang "de"), the opt-out page reached from every
// email, the window that closes after connecting an account. One main with a
// centred card (size sm 400px, md 480px), one footer with the legal line and
// its links under it.
//
//   - the card: NDI's full logo top left at 120px wide, then the title (the
//     page's h1, tabindex -1 so the router can move focus to it), the
//     description, and the content (a form, a Callout) in the body;
//   - the logo is made for light grounds: an <img> for Light, and for Dark
//     the darkSrc as a second <img>, else a span with role="img" that draws
//     the same file as a mask in ink. Its height is 120 over the logo's
//     ratio (NDI's 1111 / 620 by default), rounded; its alt "NDI New Digital
//     Intelligence" by default. The system's AuthLayout draws this itself —
//     it does not use LogoMark (nav.ts, class sdr-logo-mark), so neither does
//     the port;
//   - the footer is there with a legal line or at least one link; each link
//     is crumbLink() from page-header.ts, as in the system: an <a> with href
//     (onClick for the router), else a button.
//
// A template function: the system's AuthLayout holds no state. The props keep
// the system's names; the system's children are the second argument,
// `content`. Every React-node prop goes through node().
// ---------------------------------------------------------------------------

import { html, type TemplateResult } from 'lit';
import { ifDefined } from 'lit/directives/if-defined.js';

import { cx, node, present } from './helpers.js';
import { crumbLink, type PageLink } from './page-header.js';

/** The full NDI logo. Light grounds only: in Dark, darkSrc if given, else the same file as a mask in ink. */
export interface LogoSource {
  src: string;
  alt?: string;
  darkSrc?: string;
  /** Width over height; NDI's 1111 / 620 when not given. */
  ratio?: number;
}

export interface AuthLayoutProps {
  /** The page's h1: "Sign in to SDR Agent", "Stop contact". */
  title?: unknown;
  description?: unknown;
  logo?: LogoSource;
  /** "© 2026 New Digital Intelligence GmbH" */
  legal?: unknown;
  /** Datenschutzerklärung, Impressum. */
  links?: PageLink[];
  lang?: string;
  /** sm: a 400px card (default), md: 480px. */
  size?: 'sm' | 'md';
  /** The main's id, for the skip link. */
  mainId?: string;
  className?: string;
}

const NDI_LOGO_RATIO = 1111 / 620;

function brand(logo: LogoSource): TemplateResult {
  const alt = logo.alt || 'NDI New Digital Intelligence';
  const ratio = logo.ratio || NDI_LOGO_RATIO;
  const src = `url("${logo.src}")`;
  const height = Math.round(120 / ratio);
  // A src left out (or null) writes no src, as React does; Lit would write src="".
  return html`<div class="sdr-auth__brand">
    <img
      class="sdr-auth__logo sdr-auth__logo--light"
      src=${ifDefined(logo.src)}
      alt=${alt}
      width="120"
      height=${height}
    />
    ${
      logo.darkSrc
        ? html`<img
            class="sdr-auth__logo sdr-auth__logo--dark"
            src=${logo.darkSrc}
            alt=${alt}
            width="120"
            height=${height}
          />`
        : html`<span
            class="sdr-auth__logo sdr-auth__logo--dark sdr-auth__logo-mask"
            role="img"
            aria-label=${alt}
            style=${`aspect-ratio:${String(ratio)};-webkit-mask-image:${src};mask-image:${src}`}
          ></span>`
    }
  </div>`;
}

export function authLayout(p: AuthLayoutProps, content?: unknown): TemplateResult {
  const links = p.links || [];
  return html`<div class=${cx('sdr-auth', p.className)} lang=${ifDefined(p.lang)}>
    <main id=${ifDefined(p.mainId)} class="sdr-auth__main">
      <div class=${cx('sdr-auth__card', p.size === 'md' && 'sdr-auth__card--md')}>
        ${p.logo ? brand(p.logo) : null}
        ${present(p.title) ? html`<h1 tabindex="-1" class="sdr-auth__title">${node(p.title)}</h1>` : null}
        ${present(p.description) ? html`<p class="sdr-auth__desc">${node(p.description)}</p>` : null}
        ${present(content) ? html`<div class="sdr-auth__body">${node(content)}</div>` : null}
      </div>
    </main>
    ${
      present(p.legal) || links.length
        ? html`<footer class="sdr-auth__foot">${present(p.legal) ? html`<span>${node(p.legal)}</span>` : null}${links.map(
            l => crumbLink(l, 'sdr-auth__link', l.label),
          )}</footer>`
        : null
    }
  </div>`;
}
